import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import { useColorTheme } from "./ColorThemeContext";
import { AddIcon, SettingsIcon } from "../../../icons";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { toast } from "react-toastify";

export const defaultColorThemes = {
  default: ["#ff595e", "#ff924c", "#ffca3a", "#8ac926", "#1982c4"],
  ocean: ["#003f5c", "#2f4b7c", "#665191", "#a05195", "#d45087"],
  forest: ["#2d6a4f", "#40916c", "#52b788", "#74c69d", "#95d5b2"],
  sunset: ["#ff7c43", "#faa15c", "#f4a582", "#f7c09b", "#fde0c5"],
  berry: ["#7400b8", "#6930c3", "#5e60ce", "#5390d9", "#4ea8de"],
  earth: ["#553939", "#704F4F", "#A77979", "#C5B3B3", "#E4D4D4"],
  pastel: ["#ffadad", "#ffd6a5", "#fdffb6", "#caffbf", "#9bf6ff"],
  vintage: ["#264653", "#2a9d8f", "#e9c46a", "#f4a261", "#e76f51"],
  nordic: ["#001219", "#005f73", "#0a9396", "#94d2bd", "#e9d8a6"],
};

interface CustomColorThemeProps {
  setIsCreatingCustom: (isCreatingCustom: boolean) => void;
}

interface ColorThemeSelectorProps {
  tempTheme: string;
  setTempTheme: (tempTheme: string) => void;
}

export function ColorThemeSelector({ tempTheme, setTempTheme }: ColorThemeSelectorProps) {
  const { setSelectedTheme, colorThemes } = useColorTheme();
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);

  useEffect(() => {
    setSelectedTheme(tempTheme);
  }, []);

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "18px",
          py: "14px",
          borderBottom: "1px solid #f3f4f6",
          background: "#fafafa",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsIcon sx={{ fontSize: 14, color: "#6b7280" }} />
          </Box>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{t("Chart Color Theme")}</Typography>
        </Box>
        <button
          onClick={() => setIsCreatingCustom(!isCreatingCustom)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "5px 12px",
            borderRadius: "7px",
            border: "1px solid #e5e7eb",
            background: "#fff",
            fontSize: "12px",
            fontWeight: 500,
            color: "#374151",
            cursor: "pointer",
            transition: "all .15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "#3b82f6";
            (e.currentTarget as HTMLElement).style.color = "#3b82f6";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "#e5e7eb";
            (e.currentTarget as HTMLElement).style.color = "#374151";
          }}
        >
          <AddIcon sx={{ fontSize: 13 }} />
          {t("AddCustomTheme")}
        </button>
      </Box>

      <Box sx={{ p: "16px" }}>
        {isCreatingCustom && (
          <Box sx={{ mb: "14px" }}>
            <CustomColorTheme setIsCreatingCustom={setIsCreatingCustom} />
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "10px",
          }}
        >
          {Object.entries(colorThemes).map(([theme, colors]) => {
            const isSelected = tempTheme === theme;
            return (
              <Box
                key={theme}
                onClick={() => setTempTheme(theme)}
                sx={{
                  borderRadius: "10px",
                  border: isSelected ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                  background: isSelected ? "#f8faff" : "#fff",
                  p: "12px",
                  cursor: "pointer",
                  transition: "all .15s",
                  boxShadow: isSelected ? "0 0 0 3px rgba(59,130,246,0.1)" : "none",
                  "&:hover": {
                    borderColor: isSelected ? "#3b82f6" : "#93c5fd",
                    background: isSelected ? "#f8faff" : "#f9fafb",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "10px" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? "#3b82f6" : "#374151",
                    }}
                  >
                    {theme}
                  </Typography>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: isSelected ? "4px solid #3b82f6" : "2px solid #d1d5db",
                      background: "#fff",
                      transition: "all .15s",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: "5px" }}>
                  {(colors as string[]).slice(0, 5).map((color, index) => (
                    <Box
                      key={index}
                      sx={{
                        flex: 1,
                        height: 24,
                        backgroundColor: color,
                        borderRadius: "5px",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

const CustomColorTheme = ({ setIsCreatingCustom }: CustomColorThemeProps) => {
  const { setColorThemes, colorThemes, setSelectedTheme } = useColorTheme();
  const [theme, setTheme] = useState("");
  const [colors, setColors] = useState<string[]>(["#ff595e", "#ff924c", "#ffca3a", "#8ac926", "#1982c4"]);

  const handleAddCustomTheme = () => {
    if (theme === "") {
      toast.warning("테마 이름을 입력하세요.");
      return;
    }

    if (theme in colorThemes) {
      toast.warning("이미 존재하는 테마이름입니다.");
      return;
    }

    setIsCreatingCustom(false);
    setColorThemes({ ...colorThemes, [theme]: colors });
    setSelectedTheme(theme);
    setColors(["#ff595e", "#ff924c", "#ffca3a", "#8ac926", "#1982c4"]);
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "var(--custom-color-theme-bg)",
        border: "1px solid var(--custom-color-theme-border)",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Box>
        <TextField label={t("ThemeName")} sx={{ width: "100%" }} onChange={(e) => setTheme(e.target.value)} />
        <Typography variant="h6" sx={{ mt: 1 }}>
          {t("SelectColor")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {colors.map((color, index) => (
            <input
              key={index}
              type="color"
              value={color}
              style={{
                width: 50,
                height: 50,
                borderColor: "#ccc",
              }}
              onChange={(e) => {
                const newColors = [...colors];
                newColors[index] = e.target.value;
                setColors(newColors);
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "var(--button-color)",
              color: "var(--button-text-color)",
            }}
            onClick={handleAddCustomTheme}
          >
            {t("Add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setIsCreatingCustom(false);
              setColors(["#ff595e", "#ff924c", "#ffca3a", "#8ac926", "#1982c4"]);
            }}
          >
            {t("Cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
